import type { ReactNode } from 'react'

const MUTED_CHIP_CLASS =
  'bg-[#f5f6fa] text-black-200 border-[rgb(0_0_0/5%)] rounded-[6px]'

function DottedMeta({ items, className }: { items: string[]; className: string }) {
  return (
    <span className={`flex min-w-px flex-1 shrink-0 grow basis-0 items-center gap-1.5 ${className}`}>
      {items.map((text, i) => (
        <span key={text} className="flex items-center gap-1.5">
          {i > 0 && <span className="ui-dot shrink-0" aria-hidden="true" />}
          <span>{text}</span>
        </span>
      ))}
    </span>
  )
}

export function ProblemIssueCard({
  categories,
  title,
  summary,
  icon,
  metaItems,
  actionText,
  actionMuted,
}: {
  categories: string[]
  title: string
  summary?: string | undefined
  icon?: ReactNode
  metaItems: string[]
  actionText?: string | undefined
  actionMuted?: boolean | undefined
}) {
  return (
    <div className="flex h-full w-full flex-col items-center gap-3.5 overflow-clip rounded-[14px] border border-white-200 bg-white-50 pb-[18px] shadow-[0_0_4px_-1px_rgb(0_0_0/6%),0_0_12px_-4px_rgb(0_0_0/12%)]">
      <div className="flex w-full items-start bg-[rgb(237_238_248/90%)] px-5 py-[18px]">
        <div className="flex min-w-px flex-1 items-center gap-2">
          <div className="flex min-w-px flex-1 flex-col items-start gap-2">
            {categories.length > 0 && (
              <div className="flex items-center gap-2">
                {categories.map((label) => (
                  <span
                    key={label}
                    className="inline-flex shrink-0 items-center justify-center rounded-lg border border-[rgb(0_0_0/5%)] bg-violet-800 px-3 py-[5px] text-body-sm font-medium whitespace-nowrap text-white-50"
                  >
                    {label}
                  </span>
                ))}
              </div>
            )}
            <p className="m-0 w-full overflow-hidden text-ellipsis whitespace-nowrap text-body-lg-reading font-medium text-black-800">
              {title}
            </p>
          </div>
          {icon !== undefined && (
            <span className="flex h-14 w-[60px] shrink-0 items-center justify-center" aria-hidden="true">
              {icon}
            </span>
          )}
        </div>
      </div>

      <div className="flex w-full flex-col items-start gap-5 px-5">
        <p className="m-0 line-clamp-2 min-h-[3.4em] w-full text-body-md-reading font-normal text-black-800">
          {summary}
        </p>
        <div className="flex w-full items-center gap-1.5">
          <DottedMeta items={metaItems} className="text-body-sm-reading font-normal text-black-100" />
          {actionText !== undefined && (
            <span
              className={`inline-flex shrink-0 items-center justify-center rounded-md border px-3 py-1 text-body-sm font-medium whitespace-nowrap ${
                actionMuted === true
                  ? MUTED_CHIP_CLASS
                  : 'border-[rgb(0_0_0/5%)] bg-violet-100 text-violet-700'
              }`}
            >
              {actionText}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export function ProblemListCard({
  categories,
  title,
  summary,
  metaItems,
  actionText,
  actionMuted,
}: {
  categories: string[]
  title: string
  summary?: string | undefined
  metaItems: string[]
  actionText?: string | undefined
  actionMuted?: boolean | undefined
}) {
  return (
    <div className="flex h-full w-full flex-col items-start gap-3 overflow-clip rounded-[14px] border border-white-200 bg-white-50 p-5 shadow-[0_0_4px_-1px_rgb(0_0_0/6%),0_0_12px_-4px_rgb(0_0_0/12%)]">
      <div className="flex w-full flex-1 flex-col items-start gap-3">
        {categories.length > 0 && (
          <div className="flex items-center gap-2">
            {categories.map((label) => (
              <span
                key={label}
                className="inline-flex shrink-0 items-center justify-center rounded-lg border border-[rgb(0_0_0/5%)] bg-violet-900 px-3 py-[3px] text-body-md font-medium whitespace-nowrap text-white-50"
              >
                {label}
              </span>
            ))}
          </div>
        )}
        <div className="flex w-full flex-col items-start gap-2">
          <p className="m-0 w-full text-body-lg-reading font-semibold text-black-800">{title}</p>
          <p className="m-0 line-clamp-2 min-h-[3.4em] w-full text-body-md-reading font-normal text-black-700">
            {summary}
          </p>
        </div>
      </div>
      <div className="flex w-full items-center gap-1.5">
        <DottedMeta items={metaItems} className="text-body-sm-reading font-normal text-black-100" />
        {actionText !== undefined && (
          <span
            className={`inline-flex shrink-0 items-center justify-center rounded-md border px-3 py-1 text-body-sm font-medium whitespace-nowrap ${
              actionMuted === true
                ? MUTED_CHIP_CLASS
                : 'border-[rgb(0_0_0/5%)] bg-yellow-50 text-yellow-800'
            }`}
          >
            {actionText}
          </span>
        )}
      </div>
    </div>
  )
}

export function SolutionListCard({
  profileIcon,
  profileTone,
  orgName,
  categories,
  title,
  summary,
  notice,
  noticeIcon,
  metaItems,
  actionText,
  actionMuted,
}: {
  profileIcon: ReactNode
  profileTone?: string | undefined
  orgName: string
  categories: string[]
  title: string
  summary?: string | undefined
  notice?: string | undefined
  noticeIcon?: ReactNode
  metaItems: string[]
  actionText?: string | undefined
  actionMuted?: boolean | undefined
}) {
  return (
    <div className="flex h-full w-full flex-col items-start gap-5">
      <div className="flex min-w-0 items-center gap-2">
        <span
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center overflow-clip rounded-[99px] border border-[rgb(0_0_0/5%)] bg-violet-100 text-black-100 [&>svg]:opacity-50"
          {...(profileTone !== undefined ? { style: { background: profileTone } } : {})}
        >
          {profileIcon}
        </span>
        <span className="min-w-0 truncate text-body-md font-semibold text-black-600">{orgName}</span>
      </div>

      <div className="flex w-full flex-1 flex-col items-start gap-[18px]">
        <div className="flex w-full flex-col items-start gap-2">
          <p className="m-0 w-full truncate text-body-lg-reading font-semibold text-black-800">
            {title}
          </p>
          {summary !== undefined && summary.length > 0 && (
            <p className="m-0 line-clamp-3 min-h-[5.1em] w-full text-body-md-reading font-normal text-black-800">
              {summary}
            </p>
          )}
        </div>

        {categories.length > 0 && (
          <div className="flex w-full flex-wrap items-center gap-2">
            {categories.map((label) => (
              <span
                key={label}
                className="inline-flex shrink-0 items-center justify-center rounded-lg border border-[rgb(0_0_0/5%)] bg-white-200 px-3 py-[5px] text-body-sm font-medium whitespace-nowrap text-black-300"
              >
                {label}
              </span>
            ))}
          </div>
        )}

        <span className="flex min-h-5 items-center gap-1 text-body-sm font-normal text-violet-600 [&>svg]:h-5 [&>svg]:w-5 [&>svg]:shrink-0">
          {notice !== undefined && notice.length > 0 && (
            <>
              {noticeIcon}
              <span>{notice}</span>
            </>
          )}
        </span>
      </div>

      {(metaItems.length > 0 || actionText !== undefined) && (
        <div className="flex w-full items-center gap-1.5">
          <DottedMeta items={metaItems} className="text-label font-normal text-black-100" />
          {actionText !== undefined && (
            <span
              className={`inline-flex shrink-0 items-center justify-center rounded-md border px-3 py-[3px] text-body-sm-reading font-medium whitespace-nowrap ${
                actionMuted === true
                  ? MUTED_CHIP_CLASS
                  : 'border-[rgb(0_0_0/5%)] bg-yellow-50 text-yellow-800'
              }`}
            >
              {actionText}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
